/**
 * ARIA Scheduled Jobs Page
 * Version: 1.0.0
 *
 * ARIA-4: Scheduled tab (chronological order, exclude today's jobs)
 */

import { useQuery, gql } from '@apollo/client';
import { MapPin, User, Clock } from 'lucide-react';
import type { Job } from '../types';
import { IdentifierBadge } from '../components/jobs/IdentifierBadge';

const SCHEDULED_JOBS_QUERY = gql`
  query ScheduledJobs($excludeToday: Boolean) {
    jobsScheduled(excludeToday: $excludeToday) {
      id
      sytelineOrderNumber
      seNumber
      customerName
      fullAddress
      city
      jobType
      identifierType
      scheduling {
        scheduledDate
        startTime
        endTime
        appointmentDescription
      }
      technician {
        id
        fullName
      }
      jobSummary
      description
    }
  }
`;

export default function ScheduledJobs() {
  // ARIA-4: Exclude today's jobs by default
  const { data, loading } = useQuery(SCHEDULED_JOBS_QUERY, {
    variables: { excludeToday: true },
  });

  const jobs = (data?.jobsScheduled || []) as Job[];

  // Group jobs by date
  const jobsByDate = jobs.reduce(
    (acc, job) => {
      const date = job.scheduling?.scheduledDate
        ? new Date(job.scheduling.scheduledDate).toDateString()
        : 'Unscheduled';
      if (!acc[date]) acc[date] = [];
      acc[date].push(job);
      return acc;
    },
    {} as Record<string, Job[]>
  );

  // Sort dates chronologically (ARIA-4)
  const sortedDates = Object.keys(jobsByDate).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Scheduled Jobs</h1>
        <p className="text-gray-600">
          Upcoming scheduled appointments (excluding today)
        </p>
      </header>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading scheduled jobs...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No scheduled jobs found</div>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((date) => (
            <div key={date} className="card overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">
                  {new Date(date).toLocaleDateString('en-CA', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </h2>
                <p className="text-sm text-gray-500">{jobsByDate[date].length} job(s)</p>
              </div>

              <div className="divide-y divide-gray-200">
                {jobsByDate[date].map((job) => (
                  <div key={job.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {job.seNumber || job.sytelineOrderNumber}
                          </span>
                          <IdentifierBadge type={job.identifierType} />
                        </div>
                        <p className="text-sm text-gray-600">{job.customerName}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.city}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {job.scheduling?.startTime
                              ? new Date(job.scheduling.startTime).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : 'TBD'}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {job.technician?.fullName || 'Unassigned'}
                          </span>
                        </div>
                        {(job.scheduling?.appointmentDescription || job.jobSummary) && (
                          <p className="mt-2 text-sm text-gray-600">
                            {job.scheduling?.appointmentDescription || job.jobSummary}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
